import { useState, useEffect, useCallback } from 'react';
import PublicAPI from '@/api/TreedisPublicDAO';
import { getTokenData, getTourTypeFromUrl } from '../utils';
import getSlugFromUrl from '@/utils/getSlugFromUrl';

export enum SSOProviders {
  OKTA = 'okta',
  SALM = 'salm',
}

interface UseSSOLoginProps {
  tour?: any;
  onLoginSuccess?: (userId: number) => void;
}

const useSSOLogin = ({ tour, onLoginSuccess }: UseSSOLoginProps = {}) => {
  const [SSORedirectURL, setSSORedirectURL] = useState('');
  const [enabledSSO, setEnabledSSO] = useState(false);

  const createLoginUrl = useCallback(async (providerId: number, data: Record<string, unknown> | string) => {

    try {
      const response = await PublicAPI.createLoginUrl(providerId, {
        data,
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return { state: '', loginUrl: '' };
    }
  }, []);

  // Handle SSO token in URL
  useEffect(() => {
    const url = new URL(window.location.href);
    const publicUserToken = url.searchParams.get('publicUserToken');

    if (publicUserToken) {
      localStorage.setItem('publicUserToken', publicUserToken);

      // Clean up the URL
      url.searchParams.delete('accessToken');
      url.searchParams.delete('publicUserToken');
      window.history.replaceState({}, '', url.toString());

      // Extract user ID from token
      const { userIdFromToken } = getTokenData(publicUserToken);
      if (userIdFromToken && onLoginSuccess) {
        onLoginSuccess(Number(userIdFromToken));
      }
    }
  }, [onLoginSuccess]);

  // Initialize SSO settings
  useEffect(() => {
    let isMounted = true;

    const initializeSSOSettings = async () => {
      try {
        const { data: result } = await PublicAPI.getSSOSettings(window.location.hostname);

        if (!isMounted) return;

        const isValidOktaConfig = result && result.isEnabled && result.credentials.domain && result.credentials.clientId && result.ssoProvider.code === SSOProviders.OKTA;

        const isValidSAMLConfig =
          result &&
          result.isEnabled &&
          result.credentials.sp.certificate &&
          result.credentials.idp.certificate &&
          result.credentials.idp.loginUrl &&
          result.ssoProvider.code === SSOProviders.SALM;

        const isValidConfig = isValidOktaConfig || isValidSAMLConfig;
        if (isValidConfig) {
          const domains = result?.allowedDomains || [];

          const isAllowedDomain = domains.length === 0 || domains.includes(window.location.host);

          setEnabledSSO(isValidConfig && isAllowedDomain);

          if (isValidConfig && isAllowedDomain) {
            const response = await createLoginUrl(result.providerId, {
              domain: window.location.hostname,
              isPublic: true,
              slug: getSlugFromUrl(window.location.href),
              tourType: getTourTypeFromUrl(window.location.href),
            });

            if (!isMounted) return;
            window.localStorage.setItem('SSOState', response.state);
            setSSORedirectURL(response.loginUrl);
          }
        }
      } catch (error) {
        console.error('Failed to initialize SSO settings:', error);
      }
    };

    initializeSSOSettings();

    return () => {
      isMounted = false;
    };
  }, [tour?.slug, createLoginUrl]);

  const handleSSOLogin = useCallback(() => {
    if (SSORedirectURL) {
      window.location.replace(SSORedirectURL);
    }
  }, [SSORedirectURL]);

  return {
    enabledSSO,
    handleSSOLogin,
    SSORedirectURL,
  };
};

export default useSSOLogin;
